import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Volume2, CheckCircle2, ArrowRight } from "lucide-react";
import { speak, speakSequence, cancelSpeech } from "@/lib/speak";

// Reusable multiple-choice engine (reading comprehension, vocabulary, etc.)
export default function McqEngine({ activity, onComplete, autoplay = false }) {
  const cfg = activity.config || {};
  const [choice, setChoice] = useState(null);
  const [readingIdx, setReadingIdx] = useState(-1); // option being narrated in autoplay
  const advanced = useRef(false);
  const answered = choice !== null;
  const correct = choice === cfg.answer;

  // Auto-narrate: read the question, then each choice aloud while highlighting it.
  useEffect(() => {
    setChoice(null); setReadingIdx(-1); advanced.current = false;
    if (autoplay) {
      const items = [{ text: cfg.question }];
      (cfg.options || []).forEach((opt, i) => {
        items.push({ text: opt, before: () => setReadingIdx(i), after: () => setReadingIdx(-1) });
      });
      const cancel = speakSequence(items, { speed: 0.9, gap: 450 });
      return () => { cancel(); cancelSpeech(); setReadingIdx(-1); };
    }
    if (cfg.speak && cfg.question) speak(cfg.question);
    return () => cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id, autoplay]);

  // Auto-advance after a correct answer in autoplay.
  useEffect(() => {
    if (autoplay && answered && correct && !advanced.current) {
      advanced.current = true;
      cancelSpeech();
      speak("Correct!", "nova", { speed: 0.95 });
      const t = setTimeout(() => onComplete(100), 1500);
      return () => clearTimeout(t);
    }
  }, [autoplay, answered, correct, onComplete]);

  const pick = (i, opt) => {
    if (answered) return;
    cancelSpeech();
    setChoice(i);
    if (cfg.speak || autoplay) speak(opt, undefined, { speed: autoplay ? 0.95 : 1 });
  };

  return (
    <div className="text-center" data-testid="mcq-engine">
      <div className="flex items-center justify-center gap-2">
        <h3 className="font-heading text-2xl font-bold text-[#1F2937]">{cfg.question}</h3>
        <button onClick={() => speak(cfg.question)} data-testid="mcq-speak" className="rounded-full p-2 text-[#0284C7] hover:bg-[#0284C7]/10">
          <Volume2 size={20} />
        </button>
      </div>

      <div className="mx-auto mt-8 flex max-w-xl flex-col gap-3">
        {(cfg.options || []).map((opt, i) => {
          const chosen = choice === i;
          const showState = answered && (i === cfg.answer || chosen);
          const good = i === cfg.answer;
          const reading = readingIdx === i;
          return (
            <motion.button
              key={i}
              whileTap={{ scale: 0.98 }}
              animate={reading ? { scale: 1.03 } : { scale: 1 }}
              disabled={answered}
              onClick={() => pick(i, opt)}
              data-testid={`mcq-option-${i}`}
              className={`rounded-2xl border-2 px-5 py-4 text-left font-body text-base sm:text-lg font-semibold transition-colors ${
                showState
                  ? good ? "border-[#10B981] bg-[#10B981]/10 text-[#047857]" : "border-[#E05A47] bg-[#E05A47]/10 text-[#E05A47]"
                  : reading ? "border-[#0284C7] bg-[#0284C7]/10 text-[#0284C7] shadow-md"
                  : "border-[#E5DECF] bg-white text-[#1F2937] hover:border-[#0284C7]"
              }`}>
              <span className="inline-flex items-center gap-2">{reading && <Volume2 size={16} className="animate-pulse" />}{opt}</span>
            </motion.button>
          );
        })}
      </div>

      <div className="mt-10">
        {answered && correct && (
          <button onClick={() => onComplete(100)} data-testid="mcq-continue"
            className="inline-flex items-center gap-2 rounded-full bg-[#10B981] px-8 py-3 font-heading font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95">
            <CheckCircle2 size={18} /> Correct! Next <ArrowRight size={18} />
          </button>
        )}
        {answered && !correct && (
          <div>
            <p className="font-body text-[#E05A47] font-semibold">Not quite — try again!</p>
            <button onClick={() => { cancelSpeech(); setChoice(null); }} data-testid="mcq-retry" className="mt-3 rounded-full bg-[#F59E0B] px-8 py-3 font-heading font-bold text-white">Try again</button>
          </div>
        )}
      </div>
    </div>
  );
}
